import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Download, FileSpreadsheet, FolderOpen, AlertTriangle, Check, ChevronsUpDown, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Reports() {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectSelectOpen, setProjectSelectOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const downloadExcel = async (endpoint, filename, key) => {
    setDownloading({ ...downloading, [key]: true });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year },
        responseType: 'blob'
      });
      
      // Create blob with explicit type
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel file');
    } finally {
      setDownloading({ ...downloading, [key]: false });
    }
  };

  const downloadLowStockExcel = async () => {
    setDownloading({ ...downloading, lowStock: true });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/reports/low-stock-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'low_stock_items.xlsx');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel file');
    } finally {
      setDownloading({ ...downloading, lowStock: false });
    }
  };

  const downloadProjectDataExcel = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    setDownloading({ ...downloading, projectData: true });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/reports/project-data-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { westaw_project_number: selectedProject },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${selectedProject}_data.xlsx`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Project data downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download project data');
    } finally {
      setDownloading({ ...downloading, projectData: false });
    }
  };

  const getSelectedProjectDisplay = () => {
    const project = projects.find(p => p.westaw_project_number === selectedProject);
    return project ? `${project.westaw_project_number} - ${project.client_name}` : 'Search and select a project...';
  };

  const getMonthName = () => {
    return months.find(m => m.value === month)?.label || '';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="reports-title">Reports</h1>
        <p className="text-slate-600 mt-2">Download monthly reports as Excel files</p>
      </div>

      {/* Month/Year Selection */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-xl font-semibold">Select Period</CardTitle>
          <CardDescription>Choose the month and year for your reports</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={month.toString()} onValueChange={(val) => setMonth(parseInt(val))}>
                <SelectTrigger data-testid="report-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Year</Label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                data-testid="report-year-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Download Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Monthly Consumption Details */}
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ClipboardList className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">Monthly Consumption Details</CardTitle>
                <CardDescription>Project-wise stock issues with costs</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                <strong>Period:</strong> {getMonthName()} {year}
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Project-wise stock issues</li>
                <li>• Date of issue per item</li>
                <li>• Item cost per issue</li>
                <li>• Summary totals by project</li>
              </ul>
              <Button 
                onClick={() => downloadExcel('/api/reports/monthly-consumption-excel', `monthly_consumption_details_${getMonthName()}_${year}.xlsx`, 'consumption')}
                disabled={downloading.consumption}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="download-consumption-button"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading.consumption ? 'Downloading...' : 'Download Excel'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock Report */}
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">Low Stock Items</CardTitle>
                <CardDescription>Items below reorder level</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700">
                <strong>Note:</strong> Current snapshot (not date-filtered)
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Items below reorder level</li>
                <li>• Current stock quantity</li>
                <li>• Reorder level</li>
                <li>• Unit rate from last receipt</li>
              </ul>
              <Button 
                onClick={downloadLowStockExcel}
                disabled={downloading.lowStock}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="download-low-stock-button"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading.lowStock ? 'Downloading...' : 'Download Excel'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-amber-900">Excel Report Format</h4>
                <p className="text-sm text-amber-700 mt-1">
                  All reports are downloaded as Excel (.xlsx) files. Reports include formatted headers, summary rows, and auto-sized columns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Data Report */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FolderOpen className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold">Project Data Report</CardTitle>
              <CardDescription>Download all data for a specific WESTAW Project Number</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Select WESTAW Project</Label>
              <Popover open={projectSelectOpen} onOpenChange={setProjectSelectOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={projectSelectOpen}
                    className="w-full justify-between font-normal"
                    data-testid="project-data-select"
                  >
                    <span className={selectedProject ? "text-slate-900" : "text-slate-500"}>
                      {getSelectedProjectDisplay()}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search projects..." />
                    <CommandList>
                      <CommandEmpty>No project found.</CommandEmpty>
                      <CommandGroup>
                        {projects.map((project) => (
                          <CommandItem
                            key={project.id}
                            value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                            onSelect={() => {
                              setSelectedProject(project.westaw_project_number);
                              setProjectSelectOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedProject === project.westaw_project_number ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{project.westaw_project_number}</span>
                              <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="opacity-0">Action</Label>
              <Button 
                onClick={downloadProjectDataExcel}
                disabled={!selectedProject || downloading.projectData}
                className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300"
                data-testid="download-project-data-button"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading.projectData ? 'Downloading...' : 'Download All Project Data'}
              </Button>
            </div>
          </div>
          <div className="mt-4 bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
            <strong>Includes:</strong> Project Info, Stock Issues (with item prices), Material Reconciliation, Turned Parts Stock-Out (with part costs), and Summary
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Reports;
